/**
 * Enhanced Agent Client with proper SSE buffering
 *
 * FIXES: "Unterminated string in JSON" errors when SSE data is split across chunks
 */

import { IntelligentClient } from "@intelligenic/agent-client";

interface AgentClientConfig {
    baseURL: string;
    apiKey?: string;
    timeout?: number;
    headers?: Record<string, string>;
}

interface StreamCallbacks {
    onChunk?: (content: string) => void;
    onComplete?: (data: any) => void;
    onError?: (error: Error) => void;
}

interface ChatRequest {
    message: string;
    session_id?: string;
    agent_type: string;
    stream?: boolean;
}

/**
 * Enhanced Agent Client that properly buffers SSE data before parsing
 */
export class EnhancedAgentClient extends IntelligentClient {
    private clientConfig: AgentClientConfig;

    constructor(config: AgentClientConfig) {
        super(config);
        this.clientConfig = config;
    }

    /**
     * Override streamMessage to buffer SSE data properly
     */
    async streamMessage(request: ChatRequest, callbacks: StreamCallbacks): Promise<void> {
        const { onChunk, onComplete, onError } = callbacks;
        let buffer = "";

        try {
            const response = await fetch(`${this.clientConfig.baseURL}/api/v1/chat/stream`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(this.clientConfig.apiKey && { Authorization: `Bearer ${this.clientConfig.apiKey}` }),
                    ...this.clientConfig.headers
                },
                body: JSON.stringify({ ...request, stream: true })
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();

            if (!reader) {
                throw new Error("Response body is not readable");
            }

            while (true) {
                const { done, value } = await reader.read();

                if (done) {
                    if (buffer.trim()) {
                        this.processSSELines(buffer, onChunk, onComplete);
                    }
                    onComplete?.({ type: "complete" });
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop() || "";

                for (const line of lines) {
                    this.processSSELine(line, onChunk, onComplete);
                }
            }
        } catch (error) {
            console.error("[EnhancedAgentClient] Stream error:", error);
            onError?.(error as Error);
            throw error;
        }
    }

    private processSSELines(data: string, onChunk?: (content: string) => void, onComplete?: (data: any) => void): void {
        for (const line of data.split("\n")) {
            this.processSSELine(line, onChunk, onComplete);
        }
    }

    private processSSELine(line: string, onChunk?: (content: string) => void, onComplete?: (data: any) => void): void {
        const trimmed = line.trim();
        if (!trimmed || !trimmed.startsWith("data: ")) return;

        const data = trimmed.slice(6);
        if (data === "[DONE]") {
            onComplete?.({ type: "complete" });
            return;
        }

        try {
            const parsed = JSON.parse(data);
            if (parsed.type === "chunk") onChunk?.(parsed.content);
            else if (parsed.type === "metadata" || parsed.type === "complete") onComplete?.(parsed);
        } catch (e) {
            console.warn("[EnhancedAgentClient] Parse warning:", data.substring(0, 50));
        }
    }
}

export default EnhancedAgentClient;
